import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, TrendingUp, Shield, Award } from 'lucide-react';
import ContactFormPopup from './ContactFormPopup';

const HeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);

  const heroImages = [
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776770235/Hero_section_Nava2_ficxkh.jpg',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776770235/Hero_section_Nava-1_si39bl.jpg',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776770235/Hero_section_Nava3_ae4fou.jpg',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776770235/Hero_section_Nava4_ze1yl8.jpg',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776770235/Hero_section_Nava10_nmaipg.png',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776770235/Hero_section_Nava6_j9wlwf.png',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776770236/Hero_section_Nava7_tefhum.png',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776770236/Hero_section_Nava9_gnxewe.png',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776770236/Hero_section_Nava8_dewb3e.png',
    'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776770236/Hero_section_Nava5_kfwts7.png',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const stats = [
    { icon: Shield, value: '100%', label: 'Trust & Transparency' },
    { icon: TrendingUp, value: '7+', label: 'Years Experience' },
    { icon: Building2, value: '500+', label: 'Properties' },
    { icon: Award, value: '100+', label: 'Happy Investors' }
  ];

  return (
    <>
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-white pt-24">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 left-10 w-72 h-72 bg-yellow-100 rounded-full blur-3xl opacity-30"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-50 rounded-full blur-3xl opacity-40"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Crossfade image slideshow */}
            <motion.div
              className="relative h-[600px]"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={heroImages[currentImageIndex]}
                    alt={`Dubai ${currentImageIndex + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                  />
                </AnimatePresence>

                {/* Image overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                {/* Image indicators */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        index === currentImageIndex
                          ? 'bg-yellow-400 w-8'
                          : 'bg-white/50 w-4 hover:bg-white/80'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Floating golden particles */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-yellow-400 rounded-full"
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [0, -30, 0],
                    opacity: [0, 1, 0],
                    scale: [0, 1.5, 0],
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </motion.div>

            {/* Right side - Motivating text */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <motion.h1
                className="text-5xl md:text-7xl font-bold leading-tight"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <span className="block text-gray-800">Transform Your</span>
                <span className="block bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent mt-2">
                  Financial Future
                </span>
                <span className="block text-gray-800 mt-2">in Dubai</span>
              </motion.h1>

              <motion.p
                className="text-xl md:text-2xl text-gray-700 leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                Dubai isn't just a city—it's a{' '}
                <span className="text-yellow-600 font-semibold">global wealth magnet</span>.
                With 7+ years of expertise, we don't rely on luck. We deliver
                <span className="text-yellow-600 font-semibold"> practical, research-driven</span> investment
                opportunities that create lasting wealth.
              </motion.p>

              <motion.div
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                <motion.button
                  onClick={() => setShowPopup(true)}
                  className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Start Your Journey
                </motion.button>
                
                <motion.a
                  href="#areas"
                  className="px-8 py-4 border-2 border-yellow-500 text-yellow-700 font-bold text-lg rounded-lg hover:bg-yellow-50 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Explore Opportunities
                </motion.a>
              </motion.div>

              {/* Stats */}
              <motion.div
                className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-gray-200"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
              >
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={index}
                      className="text-center"
                      whileHover={{ scale: 1.1, y: -5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <Icon className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                      <div className="text-3xl font-bold text-yellow-600">{stat.value}</div>
                      <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-yellow-500 rounded-full flex justify-center pt-2">
            <motion.div
              className="w-1 h-2 bg-yellow-500 rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      <ContactFormPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        formType="general"
      />
    </>
  );
};

export default HeroSection;
